import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../stores/store';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
    Chip, Button, CircularProgress, Card, CardContent,
    Typography, Alert, Tooltip, Dialog, DialogTitle, DialogContent,
    DialogActions, TextField, IconButton, Box, Collapse, Divider,
    Table, TableBody, TableCell, TableHead, TableRow
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import ChecklistIcon from '@mui/icons-material/Checklist';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TuneIcon from '@mui/icons-material/Tune';
import CloseIcon from '@mui/icons-material/Close';
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import { useSetMenu } from "../../../hooks/useSetMenu";
import { useSnackbar } from 'notistack';

const MyCriteria = () => {
    const axiosPrivate = useAxiosPrivate();
    const { mainStore } = useStore();
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    useSetMenu(mainStore.MyCriteriaMenuType, mainStore.MyCriteriaMenuNo);

    const [loading, setLoading] = useState(true);
    const [criteria, setCriteria] = useState([]);
    const [pendingRequests, setPendingRequests] = useState([]);
    const [pendingValueRequests, setPendingValueRequests] = useState([]);
    const [expandedCriteria, setExpandedCriteria] = useState({});

    // Delete dialog state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deletingItem, setDeletingItem] = useState(null);
    const [deleteReason, setDeleteReason] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // Edit dialog state
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingCriteria, setEditingCriteria] = useState(null);
    const [editName, setEditName] = useState('');

    // Manage values dialog state
    const [manageValuesDialogOpen, setManageValuesDialogOpen] = useState(false);
    const [managingCriteria, setManagingCriteria] = useState(null);
    const [newValueInput, setNewValueInput] = useState('');
    const [newValuesToAdd, setNewValuesToAdd] = useState([]);

    // Delete value dialog state
    const [deleteValueDialogOpen, setDeleteValueDialogOpen] = useState(false);
    const [deletingValue, setDeletingValue] = useState(null);
    const [deleteValueReason, setDeleteValueReason] = useState('');

    // Fetch user's approved criteria with values
    const fetchCriteria = useCallback(async () => {
        try {
            const response = await axiosPrivate.get('/Criterias/MyCriteria/WithValues');
            setCriteria(response.data);
        } catch (error) {
            console.error('Error fetching criteria:', error);
            enqueueSnackbar(t('errorMessageError'), { variant: 'error' });
        }
    }, [axiosPrivate, enqueueSnackbar, t]);

    // Fetch pending requests for criteria
    const fetchPendingRequests = useCallback(async () => {
        try {
            const response = await axiosPrivate.get('/Criterias/MyCriteria/AllRequests');
            const pendingOnly = response.data.filter(r => r.status === 'Pending');
            setPendingRequests(pendingOnly);
        } catch (error) {
            console.error('Error fetching pending requests:', error);
        }
    }, [axiosPrivate]);

    // Fetch pending requests for criteria values
    const fetchPendingValueRequests = useCallback(async () => {
        try {
            const response = await axiosPrivate.get('/CriteriaValues/MyCriteriaValues/AllRequests');
            const pendingOnly = response.data.filter(r => r.status === 'Pending');
            setPendingValueRequests(pendingOnly);
        } catch (error) {
            console.error('Error fetching pending value requests:', error);
        }
    }, [axiosPrivate]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            await Promise.all([fetchCriteria(), fetchPendingRequests(), fetchPendingValueRequests()]);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }, [fetchCriteria, fetchPendingRequests, fetchPendingValueRequests]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Check if a criteria has a pending request
    const hasPendingRequest = useCallback((criteriaId) => {
        return pendingRequests.some(r => r.originalCriteriaId === criteriaId);
    }, [pendingRequests]);

    const getPendingRequestType = useCallback((criteriaId) => {
        const request = pendingRequests.find(r => r.originalCriteriaId === criteriaId);
        return request?.requestType;
    }, [pendingRequests]);

    // Check if a criteria value has a pending request
    const hasValuePendingRequest = useCallback((valueId) => {
        return pendingValueRequests.some(r => r.originalCriteriaValueId === valueId);
    }, [pendingValueRequests]);

    const getValuePendingRequestType = useCallback((valueId) => {
        const request = pendingValueRequests.find(r => r.originalCriteriaValueId === valueId);
        return request?.requestType;
    }, [pendingValueRequests]);

    // Toggle expanded state
    const toggleExpand = (criteriaId) => {
        setExpandedCriteria(prev => ({
            ...prev,
            [criteriaId]: !prev[criteriaId]
        }));
    };

    // Handle delete request dialog open
    const handleDeleteClick = (item) => {
        if (hasPendingRequest(item.id)) {
            enqueueSnackbar(t('hasPendingRequest'), { variant: 'warning' });
            return;
        }
        setDeletingItem(item);
        setDeleteReason('');
        setDeleteDialogOpen(true);
    };

    // Submit delete request
    const handleDeleteSubmit = async () => {
        if (!deletingItem) return;

        setSubmitting(true);
        try {
            await axiosPrivate.post(`/Criterias/MyCriteria/${deletingItem.id}/RequestDelete`, {
                reason: deleteReason
            });
            enqueueSnackbar(t('requestDeleteSuccess'), { variant: 'success' });
            setDeleteDialogOpen(false);
            setDeletingItem(null);
            setDeleteReason('');
            fetchData();
        } catch (error) {
            console.error('Error submitting delete request:', error);
            enqueueSnackbar(t('requestDeleteError'), { variant: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    // Handle edit click
    const handleEditClick = (item) => {
        if (hasPendingRequest(item.id)) {
            enqueueSnackbar(t('hasPendingRequest'), { variant: 'warning' });
            return;
        }
        setEditingCriteria(item);
        setEditName(item.name);
        setEditDialogOpen(true);
    };

    // Submit edit request
    const handleEditSubmit = async () => {
        if (!editingCriteria || !editName.trim()) return;

        // Check if name actually changed
        if (editName.trim() === editingCriteria.name) {
            enqueueSnackbar(t('noChangesToSave'), { variant: 'info' });
            return;
        }

        setSubmitting(true);
        try {
            await axiosPrivate.post(`/Criterias/MyCriteria/${editingCriteria.id}/RequestEdit`, {
                name: editName.trim(),
                subcategoryId: editingCriteria.subcategoryId,
                categoryId: editingCriteria.categoryId,
                panelId: editingCriteria.panelId,
                weightCoefficient: editingCriteria.weightCoefficient,
                isMandatory: editingCriteria.isMandatory
            });
            enqueueSnackbar(t('requestEditSuccess'), { variant: 'success' });
            setEditDialogOpen(false);
            setEditingCriteria(null);
            setEditName('');
            fetchData();
        } catch (error) {
            console.error('Error submitting edit request:', error);
            const errorMessage = error.response?.data?.Message || t('requestEditError');
            enqueueSnackbar(errorMessage, { variant: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    // Handle manage values click
    const handleManageValuesClick = (item) => {
        setManagingCriteria(item);
        setNewValueInput('');
        setNewValuesToAdd([]);
        setManageValuesDialogOpen(true);
    };

    // Add value to list
    const handleAddValueToList = () => {
        const trimmedValue = newValueInput.trim();
        if (!trimmedValue) return;

        // Check for duplicates in new values to add
        if (newValuesToAdd.some(v => v.toLowerCase() === trimmedValue.toLowerCase())) {
            enqueueSnackbar(t('duplicateValueInList'), { variant: 'warning' });
            return;
        }

        // Check for duplicates in existing values
        if (managingCriteria?.criteriaValues?.some(cv => cv.value.toLowerCase() === trimmedValue.toLowerCase())) {
            enqueueSnackbar(t('duplicateValueExists'), { variant: 'warning' });
            return;
        }

        setNewValuesToAdd([...newValuesToAdd, trimmedValue]);
        setNewValueInput('');
    };

    // Remove value from list
    const handleRemoveValueFromList = (valueToRemove) => {
        setNewValuesToAdd(newValuesToAdd.filter(v => v !== valueToRemove));
    };

    // Submit new values
    const handleSubmitNewValues = async () => {
        if (!managingCriteria || newValuesToAdd.length === 0) return;

        setSubmitting(true);
        try {
            await axiosPrivate.post('/CriteriaValues/Submit', {
                criteriaId: managingCriteria.id,
                subcategoryId: managingCriteria.subcategoryId,
                categoryId: managingCriteria.categoryId,
                panelId: managingCriteria.panelId,
                values: newValuesToAdd
            });
            enqueueSnackbar(t('valuesSubmittedSuccess', { count: newValuesToAdd.length }), { variant: 'success' });
            setManageValuesDialogOpen(false);
            setManagingCriteria(null);
            setNewValuesToAdd([]);
            fetchData();
        } catch (error) {
            console.error('Error submitting new values:', error);
            const errorMessage = error.response?.data || t('valuesSubmittedError');
            enqueueSnackbar(typeof errorMessage === 'string' ? errorMessage : t('valuesSubmittedError'), { variant: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    // Handle delete value click
    const handleDeleteValueClick = (value, criteria) => {
        if (hasValuePendingRequest(value.id)) {
            enqueueSnackbar(t('hasPendingRequest'), { variant: 'warning' });
            return;
        }
        setDeletingValue({ ...value, criteriaName: criteria.name });
        setDeleteValueReason('');
        setDeleteValueDialogOpen(true);
    };

    // Submit delete value request
    const handleDeleteValueSubmit = async () => {
        if (!deletingValue) return;

        setSubmitting(true);
        try {
            await axiosPrivate.post(`/CriteriaValues/MyCriteriaValues/${deletingValue.id}/RequestDelete`, {
                reason: deleteValueReason
            });
            enqueueSnackbar(t('requestDeleteValueSuccess'), { variant: 'success' });
            setDeleteValueDialogOpen(false);
            setDeletingValue(null);
            setDeleteValueReason('');
            fetchData();
        } catch (error) {
            console.error('Error submitting delete value request:', error);
            enqueueSnackbar(t('requestDeleteValueError'), { variant: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    // Calculate summary stats
    const totalCriteria = criteria.length;
    const totalValues = criteria.reduce((sum, c) => sum + (c.criteriaValues?.length || 0), 0);
    const pendingEditRequests = pendingRequests.filter(r => r.requestType === 'Edit').length;
    const pendingDeleteRequests = pendingRequests.filter(r => r.requestType === 'Delete').length;

    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="mb-0">{t("myCriteriaTitle")}</h4>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/SubmitCriteria')}
                    size="small"
                >
                    {t("submitNewCriteria")}
                </Button>
            </div>

            {/* Info Alert */}
            <Alert severity="info" className="mb-3">
                {t("myCriteriaInfo")}
            </Alert>

            {/* Pending Requests Alert */}
            {pendingRequests.length > 0 && (
                <Alert severity="warning" className="mb-3" icon={<PendingActionsIcon />}>
                    {t("pendingRequests")}: {pendingRequests.length}
                    <ul className="mb-0 mt-2">
                        {pendingRequests.map(req => (
                            <li key={req.id}>
                                <strong>{req.originalCriteriaName}</strong> - {req.requestType === 'Edit' ? t('editRequest') : t('deleteRequest')}
                            </li>
                        ))}
                    </ul>
                </Alert>
            )}

            {/* Summary Cards */}
            <div className="row mb-4">
                <div className="col-md-3 col-sm-6 mb-2">
                    <Card>
                        <CardContent sx={{ py: 2 }}>
                            <Typography variant="h4" component="div" color="success.main">
                                {totalCriteria}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('myCriteria')}
                            </Typography>
                        </CardContent>
                    </Card>
                </div>
                <div className="col-md-3 col-sm-6 mb-2">
                    <Card sx={{ borderLeft: 4, borderColor: 'info.main' }}>
                        <CardContent sx={{ py: 2 }}>
                            <Typography variant="h4" component="div" color="info.main">
                                {totalValues}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('criteriaValues')}
                            </Typography>
                        </CardContent>
                    </Card>
                </div>
                <div className="col-md-3 col-sm-6 mb-2">
                    <Card sx={{ borderLeft: 4, borderColor: 'warning.main' }}>
                        <CardContent sx={{ py: 2 }}>
                            <Typography variant="h4" component="div" color="warning.main">
                                {pendingEditRequests}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('pendingEditRequest')}
                            </Typography>
                        </CardContent>
                    </Card>
                </div>
                <div className="col-md-3 col-sm-6 mb-2">
                    <Card sx={{ borderLeft: 4, borderColor: 'error.main' }}>
                        <CardContent sx={{ py: 2 }}>
                            <Typography variant="h4" component="div" color="error.main">
                                {pendingDeleteRequests}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('pendingDeleteRequest')}
                            </Typography>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Criteria List */}
            {loading ? (
                <div className="d-flex justify-content-center p-5">
                    <CircularProgress />
                </div>
            ) : criteria.length === 0 ? (
                <div className="text-center p-5">
                    <ChecklistIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="textSecondary">
                        {t('myCriteriaEmpty')}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" className="mb-3">
                        {t('myCriteriaEmptyDescription')}
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => navigate('/SubmitCriteria')}
                    >
                        {t("submitNewCriteria")}
                    </Button>
                </div>
            ) : (
                <div className="criteria-list">
                    {criteria.map((c) => {
                        const isExpanded = expandedCriteria[c.id];
                        const requestType = getPendingRequestType(c.id);
                        const isPending = hasPendingRequest(c.id);

                        return (
                            <Card key={c.id} className="mb-3" sx={{
                                border: isPending ? '2px solid' : '1px solid',
                                borderColor: isPending ? 'warning.main' : 'divider'
                            }}>
                                <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                                    {/* Criteria Header */}
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div className="d-flex align-items-center gap-2" style={{ flex: 1 }}>
                                            <IconButton
                                                size="small"
                                                onClick={() => toggleExpand(c.id)}
                                                sx={{ mr: 1 }}
                                            >
                                                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                            </IconButton>
                                            <div>
                                                <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                                                    {c.name}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {c.panelName} &gt; {c.categoryName} &gt; {c.subcategoryName}
                                                </Typography>
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            {/* Weight Coefficient Chip */}
                                            <Chip
                                                size="small"
                                                label={`W = ${c.weightCoefficient?.toFixed(2)}`}
                                                color={c.weightCoefficient === 1 ? "error" : c.weightCoefficient === 0.5 ? "warning" : "success"}
                                            />
                                            {c.isMandatory && (
                                                <Chip size="small" label={t("mandatory")} color="primary" />
                                            )}
                                            {/* Values count */}
                                            <Chip
                                                size="small"
                                                label={`${c.criteriaValues?.length || 0} ${t("values")}`}
                                                variant="outlined"
                                            />
                                            {/* Status */}
                                            {requestType === 'Edit' ? (
                                                <Chip size="small" color="warning" icon={<PendingActionsIcon />} label={t('pendingEditRequest')} />
                                            ) : requestType === 'Delete' ? (
                                                <Chip size="small" color="error" icon={<PendingActionsIcon />} label={t('pendingDeleteRequest')} />
                                            ) : (
                                                <Chip size="small" color="success" label={t('statusApproved')} />
                                            )}
                                            {/* Action Buttons */}
                                            <Tooltip title={t('manageValues')}>
                                                <IconButton
                                                    size="small"
                                                    color="info"
                                                    onClick={() => handleManageValuesClick(c)}
                                                >
                                                    <TuneIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={isPending ? t('hasPendingRequest') : t('editCriteria')}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => handleEditClick(c)}
                                                        disabled={isPending}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title={isPending ? t('hasPendingRequest') : t('requestDelete')}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleDeleteClick(c)}
                                                        disabled={isPending}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </div>
                                    </div>

                                    {/* Expanded Values Section */}
                                    <Collapse in={isExpanded}>
                                        <Box sx={{ mt: 2, pl: 5 }}>
                                            {c.criteriaValues && c.criteriaValues.length > 0 ? (
                                                <>
                                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                                        {t("criteriaValues")} ({c.criteriaValues.length})
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                        {c.criteriaValues.map((cv) => {
                                                            const valuePendingType = getValuePendingRequestType(cv.id);
                                                            const isValuePending = hasValuePendingRequest(cv.id);
                                                            return (
                                                                <Chip
                                                                    key={cv.id}
                                                                    label={cv.value}
                                                                    size="small"
                                                                    variant={isValuePending ? "filled" : "outlined"}
                                                                    color={valuePendingType === 'Delete' ? 'error' : isValuePending ? 'warning' : 'default'}
                                                                    sx={{ bgcolor: isValuePending ? undefined : '#f8fafc' }}
                                                                    onDelete={!isValuePending ? () => handleDeleteValueClick(cv, c) : undefined}
                                                                    deleteIcon={
                                                                        <Tooltip title={t('requestDeleteValue')}>
                                                                            <DeleteIcon fontSize="small" />
                                                                        </Tooltip>
                                                                    }
                                                                />
                                                            );
                                                        })}
                                                    </Box>
                                                </>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                    {t("noValuesForCriteria")}
                                                </Typography>
                                            )}
                                            {c.pendingValuesCount > 0 && (
                                                <Alert severity="info" sx={{ mt: 2 }}>
                                                    {t("pendingValuesCount", { count: c.pendingValuesCount })}
                                                </Alert>
                                            )}
                                        </Box>
                                    </Collapse>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>{t('confirmDeleteRequest')}</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" className="mb-3">
                        {t('confirmDeleteCriteriaMessage', { name: deletingItem?.name })}
                    </Alert>
                    <Alert severity="info" className="mb-3">
                        {t('requestDeleteCriteriaInfo')}
                    </Alert>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label={t('deleteReason')}
                        placeholder={t('deleteReasonPlaceholder')}
                        value={deleteReason}
                        onChange={(e) => setDeleteReason(e.target.value)}
                        variant="outlined"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} disabled={submitting}>
                        {t('cancel')}
                    </Button>
                    <Button
                        onClick={handleDeleteSubmit}
                        color="error"
                        variant="contained"
                        disabled={submitting}
                    >
                        {submitting ? <CircularProgress size={24} /> : t('requestDelete')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Criteria Dialog */}
            <Dialog
                open={editDialogOpen}
                onClose={() => setEditDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>{t('editCriteria')}</DialogTitle>
                <DialogContent>
                    <Alert severity="info" className="mb-3 mt-2">
                        {t('editCriteriaInfo')}
                    </Alert>
                    <TextField
                        fullWidth
                        label={t('criteriaName')}
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        variant="outlined"
                        className="mt-2"
                        helperText={t('editCriteriaNameHelper')}
                    />
                    {editingCriteria && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                <strong>{t('location')}:</strong> {editingCriteria.panelName} &gt; {editingCriteria.categoryName} &gt; {editingCriteria.subcategoryName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                <strong>{t('weight')}:</strong> {editingCriteria.weightCoefficient?.toFixed(2)}
                                {editingCriteria.isMandatory && <Chip size="small" label={t('mandatory')} sx={{ ml: 1 }} />}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialogOpen(false)} disabled={submitting}>
                        {t('cancel')}
                    </Button>
                    <Button
                        onClick={handleEditSubmit}
                        color="primary"
                        variant="contained"
                        disabled={submitting || !editName.trim()}
                    >
                        {submitting ? <CircularProgress size={24} /> : t('submitEditRequest')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Manage Values Dialog */}
            <Dialog
                open={manageValuesDialogOpen}
                onClose={() => setManageValuesDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>{t('manageValues')}: {managingCriteria?.name}</span>
                        <IconButton onClick={() => setManageValuesDialogOpen(false)} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {/* Existing Values */}
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        {t('existingValues')} ({managingCriteria?.criteriaValues?.length || 0})
                    </Typography>
                    {managingCriteria?.criteriaValues && managingCriteria.criteriaValues.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3, p: 2, bgcolor: '#f8fafc', borderRadius: 1 }}>
                            {managingCriteria.criteriaValues.map((cv) => (
                                <Chip
                                    key={cv.id}
                                    label={cv.value}
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                />
                            ))}
                        </Box>
                    ) : (
                        <Alert severity="info" sx={{ mb: 3 }}>
                            {t('noValuesForCriteria')}
                        </Alert>
                    )}

                    <Divider sx={{ my: 2 }} />

                    {/* Add New Values Section */}
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        {t('addNewValues')}
                    </Typography>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        {t('addValuesInfo')}
                    </Alert>

                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label={t('newValue')}
                            placeholder={t('enterValuePlaceholder')}
                            value={newValueInput}
                            onChange={(e) => setNewValueInput(e.target.value)}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddValueToList();
                                }
                            }}
                        />
                        <Button
                            variant="outlined"
                            onClick={handleAddValueToList}
                            disabled={!newValueInput.trim()}
                        >
                            <AddIcon />
                        </Button>
                    </Box>

                    {/* Values to be submitted */}
                    {newValuesToAdd.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                                {t('valuesToSubmit')} ({newValuesToAdd.length}):
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, p: 2, border: '1px dashed', borderColor: 'primary.main', borderRadius: 1 }}>
                                {newValuesToAdd.map((value, idx) => (
                                    <Chip
                                        key={idx}
                                        label={value}
                                        size="small"
                                        color="primary"
                                        onDelete={() => handleRemoveValueFromList(value)}
                                    />
                                ))}
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setManageValuesDialogOpen(false)} disabled={submitting}>
                        {t('cancel')}
                    </Button>
                    <Button
                        onClick={handleSubmitNewValues}
                        color="primary"
                        variant="contained"
                        disabled={submitting || newValuesToAdd.length === 0}
                        startIcon={submitting ? <CircularProgress size={16} /> : <AddIcon />}
                    >
                        {t('submitValues', { count: newValuesToAdd.length })}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Value Confirmation Dialog */}
            <Dialog
                open={deleteValueDialogOpen}
                onClose={() => setDeleteValueDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>{t('confirmDeleteValueRequest')}</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" className="mb-3">
                        {t('confirmDeleteValueMessage', { value: deletingValue?.value, criteria: deletingValue?.criteriaName })}
                    </Alert>
                    <Alert severity="info" className="mb-3">
                        {t('requestDeleteValueInfo')}
                    </Alert>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label={t('deleteReason')}
                        placeholder={t('deleteValueReasonPlaceholder')}
                        value={deleteValueReason}
                        onChange={(e) => setDeleteValueReason(e.target.value)}
                        variant="outlined"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteValueDialogOpen(false)} disabled={submitting}>
                        {t('cancel')}
                    </Button>
                    <Button
                        onClick={handleDeleteValueSubmit}
                        color="error"
                        variant="contained"
                        disabled={submitting}
                    >
                        {submitting ? <CircularProgress size={24} /> : t('requestDelete')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default observer(MyCriteria);
